
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { MessageCircle } from "lucide-react";

interface MessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  professionalId: string;
  professionalName?: string;
  context?: string; // "service" ou "product"
  itemTitle?: string;
}

export const MessageDialog = ({ 
  open, 
  onOpenChange, 
  professionalId, 
  professionalName,
  context,
  itemTitle 
}: MessageDialogProps) => {
  const [message, setMessage] = useState("");
  const { session } = useAuth();

  const sendMessage = useMutation({
    mutationFn: async (content: string) => {
      if (!session?.user?.id) {
        throw new Error("Vous devez être connecté pour envoyer un message");
      }

      if (!professionalId) {
        throw new Error("ID du professionnel manquant");
      }

      const messageContent = context && itemTitle 
        ? `À propos de votre ${context === 'service' ? 'service' : 'produit'} "${itemTitle}":\n\n${content}`
        : content;

      console.log("Sending message:", {
        sender_id: session.user.id,
        receiver_id: professionalId,
        content: messageContent
      });

      const { data, error } = await supabase
        .from("messages")
        .insert({
          sender_id: session.user.id,
          receiver_id: professionalId,
          content: messageContent,
        })
        .select()
        .single();

      if (error) {
        console.error("Error sending message:", error);
        throw error;
      }

      console.log("Message sent successfully:", data);
      return data;
    },
    onSuccess: () => {
      toast.success("Message envoyé avec succès !");
      setMessage("");
      onOpenChange(false);
    },
    onError: (error: any) => {
      console.error("Error sending message:", error);
      
      if (error.message?.includes("connecté")) {
        toast.error("Vous devez être connecté pour envoyer un message");
      } else if (error.message?.includes("not found")) {
        toast.error("Professionnel introuvable");
      } else {
        toast.error("Erreur lors de l'envoi du message. Veuillez réessayer.");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!session?.user?.id) {
      toast.error("Vous devez être connecté pour envoyer un message");
      return;
    }

    if (!professionalId) {
      toast.error("Erreur: ID du professionnel manquant");
      return;
    }

    if (!message.trim()) {
      toast.error("Veuillez saisir un message");
      return;
    }

    sendMessage.mutate(message.trim());
  };

  // Vérifier si l'utilisateur est connecté
  if (!session?.user?.id) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Connexion requise
            </DialogTitle>
            <DialogDescription>
              Vous devez être connecté pour envoyer un message au professionnel.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              onClick={() => onOpenChange(false)}
              variant="outline"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Contacter le professionnel
          </DialogTitle>
          <DialogDescription>
            {professionalName ? `Envoyer un message à ${professionalName}` : "Envoyer un message au professionnel"}
            {context && itemTitle && (
              <span className="block mt-1 text-sm text-muted-foreground">
                Concernant: {itemTitle}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <Textarea
              placeholder="Tapez votre message ici..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="resize-none"
              required
            />
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={sendMessage.isPending}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={!message.trim() || sendMessage.isPending}
            >
              {sendMessage.isPending ? "Envoi..." : "Envoyer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
